class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.closeButton =
      this.querySelector('.search-modal__close-button') || this.querySelector('button[type="button"]');
    this.overlay = this.querySelector('.modal-overlay');
    this._openedAt = 0;

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });
    this.overlay?.addEventListener('click', (event) => {
      event.stopPropagation();
      if (this.isOpenGracePeriod()) return;
      this.close(false);
    });

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  isOpenGracePeriod() {
    // Ignore the leftover touch/click that opened the modal (mobile ghost dismiss).
    return Date.now() - (this._openedAt || 0) < 350;
  }

  applyHeaderSearchMetrics() {
    if (!this.classList.contains('ed-header-search')) return;
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const sheetHeight = Math.max(240, Math.round(viewportHeight - headerBottom));
    this.style.setProperty('--header-bottom', `${Math.round(headerBottom)}px`);
    this.style.setProperty('--ed-search-sheet-height', `${sheetHeight}px`);
  }

  onSummaryClick(event) {
    event.preventDefault();
    if (this.detailsContainer.classList.contains('is-closing')) return;
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (this.isOpenGracePeriod()) return;
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    if (this.detailsContainer.classList.contains('is-closing')) return;
    this.applyHeaderSearchMetrics();
    this._openedAt = Date.now();
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    // Bind on next tick so the opening click does not immediately dismiss.
    requestAnimationFrame(() => {
      document.body.addEventListener('click', this.onBodyClickEvent);
    });
    document.body.classList.add('overflow-hidden');
    this.summaryToggle.setAttribute('aria-expanded', 'true');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  getCloseAnimationTarget() {
    if (!this.classList.contains('ed-header-search')) return null;
    if (!window.matchMedia('(max-width: 749px)').matches) return null;
    return (
      this.detailsContainer.querySelector('.ed-header-search__content') ||
      this.detailsContainer.querySelector('.ed-header-search__modal')
    );
  }

  shouldAnimateMobileSearchClose() {
    return (
      this.classList.contains('ed-header-search') &&
      this.detailsContainer.hasAttribute('open') &&
      !this.detailsContainer.classList.contains('is-closing') &&
      window.matchMedia('(max-width: 749px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  close(focusToggle = true) {
    if (this.detailsContainer.classList.contains('is-closing')) return;

    if (this.shouldAnimateMobileSearchClose()) {
      const panel = this.getCloseAnimationTarget();
      this.detailsContainer.classList.add('is-closing');

      const finish = () => {
        if (this._closeAnimTimer) {
          clearTimeout(this._closeAnimTimer);
          this._closeAnimTimer = null;
        }
        if (panel) panel.removeEventListener('animationend', onEnd);
        this.detailsContainer.classList.remove('is-closing');
        this.finishClose(focusToggle);
      };

      const onEnd = (event) => {
        if (event.target !== panel) return;
        finish();
      };

      if (panel) panel.addEventListener('animationend', onEnd);
      this._closeAnimTimer = setTimeout(finish, 280);
      return;
    }

    this.finishClose(focusToggle);
  }

  finishClose(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('details-modal', DetailsModal);
